/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { csvToPlotlyMeters } from '@/lib/sensors/csv/csvToPlotlyMeters';
import {
    csvToSensorDescriptions,
    type SensorDescriptionsMap,
} from '@/lib/sensors/csv/csvToSensorDescriptions';
import { csvToTimeSeriesMeters } from '@/lib/sensors/csv/csvToTimeSeriesMeters';

/* Import types */
import type { IntervalValueRequest, LastValueRequest, StoredPlotlyRequest, TimeSeriesRequest } from '@/types/eeCheck';
import type { LabeledValueOption } from '@/types/options';
import type { PlotlyGroupConfig, PlotlyMeterConfig } from '@/types/plotlyConfig';
import type { TimeSeriesGroupConfig } from '@/types/timeSeriesConfig';

/* Import configurations */
import { CSV_SENSOR_MUENSTER, CSV_SENSOR_NEUTRAUBLING } from '@/config/csvSensors';
import {
    MUENSTER_DEVICE_SENSOR_CONFIG,
    NEUTRAUBLING_DEVICE_SENSOR_CONFIG,
} from '@/config/deviceSensorConfig';

function createPlotlyGroups(): Record<string, PlotlyGroupConfig> {
    return {
        ...(CSV_SENSOR_NEUTRAUBLING === null ? {} : {
            neutraubling: {
                label: 'Neutraubling',
                meters: csvToPlotlyMeters(CSV_SENSOR_NEUTRAUBLING),
            },
        }),
        muenster: {
            label: 'Muenster',
            meters: csvToPlotlyMeters(CSV_SENSOR_MUENSTER),
        },
    };
}

export let PLOTLY_GROUPS = createPlotlyGroups();

export type PlotlyGroupKey = 'muenster' | 'neutraubling';
export type PlotlyGroupOption = LabeledValueOption<PlotlyGroupKey>;

function createPlotlySensorDescriptions(): Partial<Record<PlotlyGroupKey, SensorDescriptionsMap>> {
    return {
        ...(CSV_SENSOR_NEUTRAUBLING === null ? {} : {
            neutraubling: csvToSensorDescriptions(CSV_SENSOR_NEUTRAUBLING),
        }),
        muenster: csvToSensorDescriptions(CSV_SENSOR_MUENSTER),
    };
}

export let PLOTLY_SENSOR_DESCRIPTIONS = createPlotlySensorDescriptions();

function createPlotlyGroupOptions(): PlotlyGroupOption[] {
    return Object.entries(PLOTLY_GROUPS).map(
    ([value, config]) => ({
        value: value as PlotlyGroupKey,
        label: config.label,
    }),
    );
}

export let PLOTLY_GROUP_OPTIONS = createPlotlyGroupOptions();

function getPreferredPlotlyMeter(group: PlotlyGroupConfig): string {
    return Object.keys(group.meters).find((meter) =>
        Object.keys(group.meters[meter].reference).some((reference) => reference !== 'pv_plots'),
    ) ?? Object.keys(group.meters)[0] ?? '';
}

export function getPreferredPlotlyReference(reference: PlotlyMeterConfig['reference']): string {
    return Object.keys(reference).find((key) => key !== 'pv_plots') ?? Object.keys(reference)[0] ?? '';
}

export let DEFAULT_PLOTLY_GROUP: PlotlyGroupKey = CSV_SENSOR_NEUTRAUBLING === null
    ? 'muenster'
    : 'neutraubling';
export let DEFAULT_PLOTLY_METER = getPreferredPlotlyMeter(PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP]);
export let DEFAULT_PLOTLY_REFERENCE = getPreferredPlotlyReference(
    PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP].meters[DEFAULT_PLOTLY_METER]?.reference ?? {},
);
export let DEFAULT_PLOTLY_RANGE =
    PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP].meters[DEFAULT_PLOTLY_METER].reference[
        DEFAULT_PLOTLY_REFERENCE
    ]?.ranges[0] ?? '';

export function getPlotlyGroupConfig(group: PlotlyGroupKey): PlotlyGroupConfig {
    return PLOTLY_GROUPS[group] ?? PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP];
}

export function getPlotlySensorDescription(
    group: PlotlyGroupKey,
    meter: string,
    subsensor?: string,
): string {
    const sensorEntry = PLOTLY_SENSOR_DESCRIPTIONS[group]?.[meter];

    if (!sensorEntry) {
        return 'No description available.';
    }

    if (subsensor) {
        return sensorEntry.subsensors[subsensor] ?? sensorEntry.description;
    }

    return sensorEntry.description;
}

export function createStoredPlotlyRequest(
    meter: string,
    reference: string,
    range: string,
): StoredPlotlyRequest {
    if (reference === 'pv_plots') {
        return createPvMonthlyLossBarsRequest(meter);
    }

    return {
        reference,
        tags: {
            meter,
            time: 'NOW',
            range,
        },
        layout: {
            title: null,
            yaxis: {
                title: null,
                ticksuffix: ' kW',
            },
            xaxis: {
                title: null,
            },
            margin: {
                t: 10,
                b: 30,
            },
        },
    };
}

export function createPvMonthlyLossBarsRequest(sensor: string): StoredPlotlyRequest {
    return {
        reference: 'pv_plots',
        tags: {
            name: 'monthly_loss_bars',
            sensor,
        },
    };
}

export function getPlotlyRequestMeter(
    group: PlotlyGroupKey,
    meter: string,
    reference: string,
    subsensor: string,
): string {
    if (reference !== 'pv_plots') {
        return subsensor ? `${meter}.${subsensor}` : meter;
    }

    if (group === 'neutraubling') {
        const deviceSensorCatalog = new DeviceSensorCatalog(NEUTRAUBLING_DEVICE_SENSOR_CONFIG);
        const { realDeviceName } = deviceSensorCatalog.resolveDeviceNames(meter);

        return `${realDeviceName}.P`;
    }

    return subsensor ? `${meter}.${subsensor}` : meter;
}

function createTimeSeriesGroups(): Record<string, TimeSeriesGroupConfig> {
    return {
        ...(CSV_SENSOR_NEUTRAUBLING === null ? {} : {
            neutraubling: {
                label: 'Neutraubling',
                meters: csvToTimeSeriesMeters(
                    CSV_SENSOR_NEUTRAUBLING,
                    NEUTRAUBLING_DEVICE_SENSOR_CONFIG,
                ),
            },
        }),
        muenster: {
            label: 'Muenster',
            meters: csvToTimeSeriesMeters(
                CSV_SENSOR_MUENSTER,
                MUENSTER_DEVICE_SENSOR_CONFIG,
            ),
        },
    };
}

export let TIME_SERIES_GROUPS = createTimeSeriesGroups();

export type TimeSeriesGroupKey = 'muenster' | 'neutraubling';
export type TimeSeriesGroupOption = LabeledValueOption<TimeSeriesGroupKey>;

let TIME_SERIES_DEVICE_SENSOR_CATALOGS: Partial<Record<
    TimeSeriesGroupKey,
    DeviceSensorCatalog
>> = {
    muenster: new DeviceSensorCatalog(MUENSTER_DEVICE_SENSOR_CONFIG),
    ...(CSV_SENSOR_NEUTRAUBLING === null ? {} : {
        neutraubling: new DeviceSensorCatalog(NEUTRAUBLING_DEVICE_SENSOR_CONFIG),
    }),
};

function createTimeSeriesGroupOptions(): TimeSeriesGroupOption[] {
    return Object.entries(TIME_SERIES_GROUPS).map(
    ([value, config]) => ({
        value: value as TimeSeriesGroupKey,
        label: config.label,
    }),
    );
}

export let TIME_SERIES_GROUP_OPTIONS = createTimeSeriesGroupOptions();

export let DEFAULT_TIME_SERIES_GROUP: TimeSeriesGroupKey = CSV_SENSOR_NEUTRAUBLING === null
    ? 'muenster'
    : 'neutraubling';
let DEFAULT_TIME_SERIES_GROUP_CONFIG: TimeSeriesGroupConfig =
    TIME_SERIES_GROUPS[DEFAULT_TIME_SERIES_GROUP]!;
export let DEFAULT_TIME_SERIES_METER =
    Object.keys(DEFAULT_TIME_SERIES_GROUP_CONFIG.meters)[0];
export let DEFAULT_TIME_SERIES_SUBSENSOR =
    DEFAULT_TIME_SERIES_GROUP_CONFIG.meters[DEFAULT_TIME_SERIES_METER].subsensors[0];

export function getTimeSeriesGroupConfig(
    group: TimeSeriesGroupKey,
): TimeSeriesGroupConfig {
    return TIME_SERIES_GROUPS[group]!;
}

/** Resolves known base sensor types through DeviceSensor and preserves special targets. */
export function getTimeSeriesSensorTarget(
    group: TimeSeriesGroupKey,
    meter: string,
    sensorType: string,
): string {
    const deviceSensorCatalog = TIME_SERIES_DEVICE_SENSOR_CATALOGS[group];

    if (!deviceSensorCatalog) {
        return `${meter}.${sensorType}`;
    }

    const deviceSensor = new DeviceSensor(meter, deviceSensorCatalog);

    if (deviceSensor.getSensorTypes(null).includes(sensorType)) {
        return deviceSensor.getSensorName(null, sensorType) ?? `${meter}.${sensorType}`;
    }

    return `${meter}.${sensorType}`;
}

export let EXAMPLE_TIME_SERIES_REQUEST: TimeSeriesRequest = {
    from: '2026-01-01 00:00:00',
    to: 'NOW',
    urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
};

export const CHECK_API_TOKEN_REQUEST: TimeSeriesRequest = {
    from: '2026-01-01 00:00:00',
    to: '2026-01-01 00:00:00',
    urn: 'WeatherHistory.Muenster.Ta',
};

export let EXAMPLE_LAST_VALUE_REQUEST: LastValueRequest = {
    timestamp: 'NOW',
    urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
};

export let EXAMPLE_INTERVAL_VALUE_REQUEST: IntervalValueRequest = {
    from: '2018-01-01',
    interval: '1year',
    to: 'NOW',
    urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
};

/** Rebuilds all CSV-derived request definitions after the active user changes. */
export function refreshEeCheckConfig(): void {
    PLOTLY_GROUPS = createPlotlyGroups();
    PLOTLY_SENSOR_DESCRIPTIONS = createPlotlySensorDescriptions();
    PLOTLY_GROUP_OPTIONS = createPlotlyGroupOptions();
    DEFAULT_PLOTLY_GROUP = CSV_SENSOR_NEUTRAUBLING === null ? 'muenster' : 'neutraubling';
    DEFAULT_PLOTLY_METER = getPreferredPlotlyMeter(PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP]);
    DEFAULT_PLOTLY_REFERENCE = getPreferredPlotlyReference(
        PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP].meters[DEFAULT_PLOTLY_METER]?.reference ?? {},
    );
    DEFAULT_PLOTLY_RANGE = PLOTLY_GROUPS[DEFAULT_PLOTLY_GROUP]
        .meters[DEFAULT_PLOTLY_METER]
        .reference[DEFAULT_PLOTLY_REFERENCE]?.ranges[0] ?? '';

    TIME_SERIES_GROUPS = createTimeSeriesGroups();
    TIME_SERIES_DEVICE_SENSOR_CATALOGS = {
        muenster: new DeviceSensorCatalog(MUENSTER_DEVICE_SENSOR_CONFIG),
        ...(CSV_SENSOR_NEUTRAUBLING === null ? {} : {
            neutraubling: new DeviceSensorCatalog(NEUTRAUBLING_DEVICE_SENSOR_CONFIG),
        }),
    };
    TIME_SERIES_GROUP_OPTIONS = createTimeSeriesGroupOptions();
    DEFAULT_TIME_SERIES_GROUP = CSV_SENSOR_NEUTRAUBLING === null ? 'muenster' : 'neutraubling';
    DEFAULT_TIME_SERIES_GROUP_CONFIG = TIME_SERIES_GROUPS[DEFAULT_TIME_SERIES_GROUP]!;
    DEFAULT_TIME_SERIES_METER = Object.keys(DEFAULT_TIME_SERIES_GROUP_CONFIG.meters)[0];
    DEFAULT_TIME_SERIES_SUBSENSOR = DEFAULT_TIME_SERIES_GROUP_CONFIG
        .meters[DEFAULT_TIME_SERIES_METER]
        .subsensors[0];
    EXAMPLE_TIME_SERIES_REQUEST = {
        from: '2026-01-01 00:00:00',
        to: 'NOW',
        urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
    };
    EXAMPLE_LAST_VALUE_REQUEST = {
        timestamp: 'NOW',
        urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
    };
    EXAMPLE_INTERVAL_VALUE_REQUEST = {
        from: '2018-01-01',
        interval: '1year',
        to: 'NOW',
        urn: `${DEFAULT_TIME_SERIES_METER}.${DEFAULT_TIME_SERIES_SUBSENSOR}`,
    };
}
